import { AxiosInstance } from 'axios';

export namespace AuthApi {
  export type ResetPasswordEntry = {
    isAdmin: boolean;
  };

  export type LoginEntry = {
    data: {
      accessCode: string;
      expiresIn: number;
      tokenType: string;
      message: string;
      mfaRequired?: boolean;
      hasTotpSecret?: boolean;
      totpSecret?: string;
      qrCodeBase64?: string;
    };
  };

  export type UserEntry = {
    id: string;
    username: string;
    roles: string[];
    firstName: string;
    lastName: string;
  };

  export type SessionEntry = {
    id: string;
    browser: string | null;
    os: string | null;
    ipAddress: string | null;
    createdAt: string;
    lastSeenAt: string;
    isCurrent: boolean;
  };

  export type LogoutOtherSessionsEntry = {
    data: {
      message: string;
    };
  };
}

export class AuthApi {
  constructor(private authApi: AxiosInstance) {}

  login(
    email: string,
    password: string,
    token: string | null | undefined
  ): Promise<AuthApi.LoginEntry> {
    return this.authApi
      .post('/api/auth/login', { email, password, token })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        throw error.response;
      });
  }

  logout() {
    this.authApi.post('/api/auth/logout');
  }

  logged(): Promise<AuthApi.UserEntry> {
    return this.authApi
      .get<{ data: AuthApi.UserEntry }>('/api/auth/user')
      .then(({ data: userEntry }) => {
        return userEntry.data;
      });
  }

  listSessions(): Promise<AuthApi.SessionEntry[]> {
    return this.authApi
      .get<{ data: AuthApi.SessionEntry[] }>('/api/auth/sessions')
      .then((response) => response.data.data)
      .catch((error) => {
        throw error.response;
      });
  }

  logoutOtherSessions(): Promise<AuthApi.LogoutOtherSessionsEntry> {
    return this.authApi
      .delete('/api/auth/sessions/others')
      .then((response) => response.data)
      .catch((error) => {
        throw error.response;
      });
  }

  resetPasswordRequest(email: string): Promise<unknown> {
    return this.authApi.post(`/api/user/reset-password-request/${email}`).catch((error) => {
      throw error.response;
    });
  }

  resetPassword(
    password: string,
    confirmPassword: string,
    token: string
  ): Promise<AuthApi.ResetPasswordEntry> {
    return this.authApi
      .put(`/api/user/reset-password/${token}`, { password, confirmPassword })
      .then((response) => response.data)
      .catch((error) => {
        throw error.response;
      });
  }

  verifyMfa(verificationCode: string): Promise<AuthApi.LoginEntry> {
    return this.authApi
      .post('/api/auth/verify-mfa', { verificationCode })
      .then((response) => response.data)
      .catch((error) => {
        throw error.response;
      });
  }

  oAuthCallback(
    provider: string,
    code: string | null,
    state: string | null,
    uiAppBaseUrl: string
  ): Promise<AuthApi.LoginEntry> {
    return this.authApi
      .post(`/api/oauth/callback/${provider}`, { code, state, uiAppBaseUrl })
      .then((response) => response.data)
      .catch((error) => {
        throw error.response;
      });
  }
}
